import React, { ReactNode } from 'react';
import { Spin, Alert, DatePicker, Switch, Empty } from 'antd';
import { ReloadOutlined, CalendarOutlined, LineChartOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import type { ComponentData } from './BaseReportingComponent.types';
import type { BaseReportingComponentState } from './useBaseReportingComponent';
import styles from './BaseReportingComponent.module.css';

const { RangePicker } = DatePicker;

type ControlsProps = {
  showControls: boolean;
  state: BaseReportingComponentState;
  onDateRangeChange: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onProjectionToggle: (checked: boolean) => void;
  onRefresh: () => void;
};

export function BaseReportingControls({
  showControls,
  state,
  onDateRangeChange,
  onProjectionToggle,
  onRefresh,
}: ControlsProps) {
  if (!showControls) return null;

  return (
    <div className={styles.controls}>
      <div className={styles.controlGroup}>
        <CalendarOutlined className={styles.controlIcon} />
        <RangePicker
          value={[
            state.dateRange.startDate ? dayjs(state.dateRange.startDate) : null,
            state.dateRange.endDate ? dayjs(state.dateRange.endDate) : null,
          ]}
          onChange={onDateRangeChange}
          size="small"
          className={styles.datePicker}
        />
      </div>

      <div className={styles.controlGroup}>
        <LineChartOutlined className={styles.controlIcon} />
        <span className={styles.controlLabel}>Projections</span>
        <Switch checked={state.showProjections} onChange={onProjectionToggle} size="small" />
      </div>

      <button
        className={styles.refreshButton}
        onClick={onRefresh}
        disabled={state.loading}
        title="Refresh data"
        type="button"
      >
        <ReloadOutlined spin={state.loading} />
      </button>
    </div>
  );
}

type BodyProps = {
  state: BaseReportingComponentState;
  children?: (data: ComponentData, loading: boolean) => ReactNode;
};

export function BaseReportingBody({ state, children }: BodyProps) {
  if (state.loading && !state.data) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
        <p className={styles.loadingText}>Loading component data...</p>
      </div>
    );
  }

  if (state.error) {
    return (
      <Alert
        type="error"
        message="Error loading data"
        description={state.error}
        showIcon
        className={styles.errorAlert}
      />
    );
  }

  if (!state.data) {
    return <Empty description="No data available" className={styles.emptyState} />;
  }

  if (children) {
    return <>{children(state.data, state.loading)}</>;
  }

  return (
    <div className={styles.defaultContent}>
      <div className={styles.dataPreview}>
        <h4>Component: {state.data.component_type}</h4>
        <p>Artifact: {state.data.artifact_name}</p>
        <p>Properties: {state.data.available_properties.length} available</p>
        <p>Historical Periods: {state.data.historical_data.length}</p>
        <p>Projection Periods: {state.data.projection_data.length}</p>
      </div>
    </div>
  );
}
